import { CampaignsApi } from '../../apis/campaigns.api';
import { CampaignModel } from '../../models/campaign.model';
import type { ICampaignCreateForm, ICampaignEditForm, ICampaignTargetingListing } from '../../models/campaign.model';
import { runInAction } from 'mobx';
import { constantCase } from 'change-case';
import { DealTypes } from '../../enums/deal-types';
import { CampaignTypes } from '../../enums/campaign-types';
import { TargetingModel } from '../../models/targeting.model';
import { BaseCrudStore } from '@monorepo/controlled/src/stores/base-crud.store';
import { CreativeSelectionModes } from '../../enums/creative-selection-modes';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { FormError } from '@monorepo/tools/src/lib/models/form-error.model';
import { HttpError } from '../../models/http-error.model';
import { whatIsWrongWithMyUrl } from '@monorepo/tools/src/lib/utils/url';
import { Statuses, Status } from '../../enums/status';
import { WhiteBlackLists } from '../../enums/white-black-list-enum';
import { getCampaignDestinationActivationError } from '../../utils/campaign-destination';
import { extractAskErrorMessage, formatFormErrorToast, getUniqueFormErrorMessages } from '../../utils/form-errors';

const getWhiteBlackListDomainsPagesError = (params: {
	whitelistDomainsPages?: Set<string>;
	blacklistDomainsPages?: Set<string>;
}): FormError | null => {
	const { whitelistDomainsPages, blacklistDomainsPages } = params;
	let property: string;
	let list: Set<string>;
	if (whitelistDomainsPages?.size && blacklistDomainsPages?.size) {
		return new FormError('black_listed_sites', "Can't be both, whitelisting and blacklisting");
	} else if (blacklistDomainsPages?.size) {
		list = blacklistDomainsPages;
		property = 'black_listed_sites';
	} else if (whitelistDomainsPages?.size) {
		list = whitelistDomainsPages;
		property = 'white_listed_sites';
	} else {
		return null;
	}
	const [domains, pages] = Array.from(list).reduce(
		(acc, item) => {
			if (item.startsWith('http')) {
				acc[1].push(item);
			} else {
				acc[0].push(item);
			}
			return acc;
		},
		[[], []] as [string[], string[]]
	);

	const invalidDomains: string[] = [];
	for (const domain of domains) {
		const { error } = whatIsWrongWithMyUrl(`https://${domain}/`);
		if (error) {
			invalidDomains.push(`${domain} => ${error}`);
		}
	}
	const invalidPages: string[] = [];
	for (const page of pages) {
		const { error } = whatIsWrongWithMyUrl(page, { allowHttp: true });
		if (error) {
			invalidPages.push(`${page} => ${error}`);
		}
	}
	if (invalidDomains.length || invalidPages.length) {
		const errors: string[] = [];
		if (invalidDomains.length) {
			errors.push(`Invalid domains: ${invalidDomains.map(item => `"${item}"`).join(', ')}`);
		}
		if (invalidPages.length) {
			errors.push(`Invalid pages: ${invalidPages.map(item => `"${item}"`).join(', ')}`);
		}
		return new FormError(property, errors.join('; '));
	}
	return null;
};

const getWhiteBlackListKeysError = (params: { whitelistKeys?: Set<string>; blacklistKeys?: Set<string> }): FormError | null => {
	const { whitelistKeys, blacklistKeys } = params;

	if (blacklistKeys?.size && whitelistKeys?.size) {
		return new FormError('black_listed_keys', "Can't be both, whitelisting and blacklisting");
	}

	return null;
};

export class CampaignCrudStore extends BaseCrudStore<CampaignModel, ICampaignCreateForm, ICampaignEditForm, HttpError> {
	isCampaignEdited = false;
	isTargetListingsEdited = false;

	constructor() {
		super({
			apiLayer: CampaignsApi,
			model: CampaignModel,
			errorModel: HttpError,
		});
	}

	createInstance() {
		return new CampaignModel({
			targeting: new TargetingModel(),
			campaign_phases: [],
			blocked_hours: [],
			status: Statuses.Paused,
			campaign_type: CampaignTypes.CPA,
			creative_selection_mode: CreativeSelectionModes.Off,
		});
	}

	async edit(data?: Partial<ICampaignEditForm>) {
		let error: HttpError | null = null;

		if (!this.isValid()) {
			return;
		}

		const campaignId = this.getData()?.getId();
		if (!campaignId) {
			runInAction(() => {
				this.setIsSuccess(false);
				// this.setHttpError(new Error('No campaign id'));
			});
			return;
		}

		this.setIsLoading(true);
		// edit campaign details
		await this.apiLayer.edit(campaignId.toString(), this.getEditFormData(data)).catch(err => {
			console.error(err);
			error = new HttpError({
				message: extractAskErrorMessage(err, 'Failed to save campaign'),
				name: 'edit campaign',
				httpStatus: 400,
				errorPayload: err,
			});
		});

		// make sure the normal edit success before try to edit the target listings
		if (!error && this.isTargetListingsEdited) {
			await CampaignsApi.editTargetListings(campaignId.toString(), this.getEditTargetListing()).catch(err => {
				console.error(err);
				error = new HttpError({
					message: extractAskErrorMessage(err, 'Failed to save the target listings only!'),
					name: 'edit campaign',
					httpStatus: 400,
					errorPayload: err,
				});
			});
		}

		const dealType = this.getData().getDealType();

		if (dealType === DealTypes.PreferredDeal || dealType === DealTypes.GuaranteedDeal) {
			const preferredDealIds = this.getPreferredDealIds();

			await CampaignsApi.attachDeals(campaignId, preferredDealIds);
		}

		if (error) {
			runInAction(() => {
				this.setHttpError(error);
				this.setIsLoading(false);
				this.setIsSuccess(false);
			});
		} else {
			runInAction(() => {
				this.setHttpError(error);
				this.setIsLoading(false);
				this.setIsSuccess(true);
			});
		}
	}

	async create() {
		const isValid = this.isValid();
		if (!isValid) {
			return;
		}
		this.setIsLoading(true);
		this.setIsSuccess(false);
		try {
			const createRes = await this.apiLayer.create(this.getCreateFormData());
			const dealType = this.getData().getDealType();
			if (dealType === DealTypes.PreferredDeal || dealType === DealTypes.GuaranteedDeal) {
				const preferredDealIds = this.getPreferredDealIds();
				if (createRes.id) {
					await CampaignsApi.attachDeals(createRes.id, preferredDealIds);
				}
			}

			this.setIsLoading(false);
			this.setIsSuccess(true);
		} catch (error: any) {
			if (error && error?.data) {
				const message =
					typeof error.data.message === 'string'
						? error.data.message
						: extractAskErrorMessage(error, 'Failed to create campaign');
				this.setHttpError(
					new HttpError({
						message,
						name: 'create campaign',
						httpStatus: error.response?.status ?? 400,
						errorPayload: error.data,
					})
				);
			}
			this.setIsLoading(false);
			this.setIsSuccess(false);
		}
	}

	public async getEnableCampaignValidationErrors(campaignIds: id[]): Promise<string[]> {
		const errorResults = await Promise.all(
			campaignIds.map(async campaignId => {
				try {
					const res = await CampaignsApi.get(campaignId);
					const campaign = new CampaignModel(res);
					const activationError = getCampaignDestinationActivationError(campaign, { campaignIsActive: true });
					if (activationError) {
						const name = campaign.getName();
						const label = name ? `${name} (${campaignId})` : String(campaignId);
						return `${label}: ${activationError}`;
					}
					return null;
				} catch {
					return `Campaign ${campaignId}: Failed to load campaign details`;
				}
			})
		);

		return errorResults.filter((error): error is string => error != null);
	}

	async enableCampaigns(campaignsIds: id[]): Promise<void> {
		const validationErrors = await this.getEnableCampaignValidationErrors(campaignsIds);
		if (validationErrors.length) {
			throw new Error(formatFormErrorToast(validationErrors));
		}

		return CampaignsApi.enableCampaigns(campaignsIds)
			.then(() => {
				this.setIsLoading(false);
				this.setIsSuccess(true);
			})
			.catch(err => {
				this.setIsLoading(false);
				this.setIsSuccess(false);
				throw new Error(extractAskErrorMessage(err, 'Failed to enable campaigns'));
			});
	}

	pauseCampaigns(campaignsIds: id[]): Promise<void> {
		return CampaignsApi.pauseCampaigns(campaignsIds)
			.then(() => {
				this.setIsLoading(false);
				this.setIsSuccess(true);
			})
			.catch(err => {
				this.setIsLoading(false);
				this.setIsSuccess(false);
				throw new Error(extractAskErrorMessage(err, 'Failed to pause campaigns'));
			});
	}

	/**
	 * Must call isValidCampaign before calling this function
	 * @returns
	 */
	public getCreateFormData(): ICampaignCreateForm & ICampaignTargetingListing {
		const campaignGroup = this.getData()?.getCampaignGroup();
		const advertiser = this.getData()?.getAdvertiser();
		const advertiserId = advertiser?.getId();
		const status = this.getData()?.getStatus();
		const parent = this.getData().getParentCampaign();
		const parentId = parent?.getId();
		const auctionType = this.getData()?.getAuctionType();
		const campaignId = this.getData().getId();
		const deepClone = this.getData().deep_clone_campaign;

		if (campaignId && deepClone) {
			deepClone.source_campaign_id = campaignId;
		}

		if (this.getData().getBannerListType() === WhiteBlackLists.Blacklist) {
			this.getData().setWhitelistedSizes([]);
		} else {
			this.getData().setBlacklistedSizes([]);
		}

		return {
			id: this.getData().getId(),
			name: this.getData().getName(),
			campaign_group: campaignGroup ? { id: campaignGroup.getId() } : null,
			advertiser: advertiserId ? { id: advertiserId } : undefined,
			status: status ? (constantCase(status) as Status) : undefined,
			parent: parentId ? { id: parentId } : undefined,
			priority: this.getData().getPriority(),
			secondary_priority: this.getData().getSecondaryPriority(),
			campaign_type: this.getData().getCampaignType(),
			auction_type: auctionType ? constantCase(auctionType) : undefined,
			deals: this.getData().getDeals(),
			billing_ids: this.getData().getBillingIds(),
			campaign_destinations: this.getData().getCampaignDestinationsFormData(),
			targeting: this.getData().getTargeting()?.getFormData(),
			blocked_hours: this.getData().getBlockedHours(),
			black_listed_sizes: this.getData().getBlacklistedSizes(),
			white_listed_sizes: this.getData().getWhitelistedSizes(),
			daily_domain_max_loss: this.getData().getDailyDomainMaxLoss(),
			allow_multiple_sales: this.getData().getAllowMultipleSales(),
			campaign_phases: this.getData()
				?.getPhases()
				.map(p => ({
					...p,
					daily_budget: p.daily_budget?.getFormData(),
					cache_key_generator: p.getCacheKeyGenerator(),
				})),

			// creatives,
			creative_selection_mode: this.getData().getCreativeSelectionMode(),
			deep_clone_campaign: deepClone,

			...this.getEditTargetListing(),
		};
	}

	/**
	 * Must call isValidCampaign before calling this function
	 * @returns
	 */
	public getEditFormData(data?: Partial<ICampaignEditForm>): ICampaignEditForm {
		const campaignGroup = this.getData()?.getCampaignGroup();
		const advertiser = this.getData()?.getAdvertiser();
		const status = this.getData()?.getStatus();
		const parent = this.getData().getParentCampaign();
		const parentId = parent?.getId();
		const auctionType = this.getData()?.getAuctionType();
		if (this.getData().getBannerListType() === WhiteBlackLists.Blacklist) {
			this.getData().setWhitelistedSizes([]);
		} else {
			this.getData().setBlacklistedSizes([]);
		}

		return {
			id: this.getData().getId(),
			name: this.getData().getName(),
			campaign_group: campaignGroup ? { id: campaignGroup.getId() } : null,
			advertiser: advertiser ? { id: advertiser.getId() } : undefined,
			status: status ? (constantCase(status) as Status) : undefined,
			parent: parentId ? { id: parentId } : undefined,
			priority: this.getData().getPriority(),
			allow_multiple_sales: this.getData().getAllowMultipleSales(),
			secondary_priority: this.getData().getSecondaryPriority(),
			campaign_type: this.getData().getCampaignType(),
			auction_type: auctionType ? constantCase(auctionType) : undefined,
			deals: this.getData().getDeals(),
			billing_ids: this.getData().getBillingIds(),
			campaign_destinations: this.getData().getCampaignDestinationsFormData(),
			targeting: this.getData().getTargeting()?.getFormData(),
			blocked_hours: this.getData().getBlockedHours(),
			campaign_phases: this.getData()
				?.getPhases()
				.map(p => ({
					...p,
					daily_budget: p.daily_budget?.getFormData(),
					cache_key_generator: p.getCacheKeyGenerator(),
				})),
			creative_selection_mode: this.getData().getCreativeSelectionMode(),
			black_listed_sizes: this.getData().getBlacklistedSizes(),
			white_listed_sizes: this.getData().getWhitelistedSizes(),
			daily_domain_max_loss: this.getData().getDailyDomainMaxLoss(),
			...data,
		};
	}

	public getDestinationActivationError(options?: { campaignIsActive?: boolean }): string | null {
		return getCampaignDestinationActivationError(this.getData(), options);
	}

	public getFormErrorMessages(): string[] {
		return getUniqueFormErrorMessages(this.formStore.getErrors());
	}

	public validateForSubmit(options?: { isDuplicate?: boolean }): { valid: boolean; messages: string[] } {
		const valid = this.isValid(options);
		return {
			valid,
			messages: this.getFormErrorMessages(),
		};
	}

	public getEditTargetListing(): ICampaignTargetingListing {
		return {
			black_listed_sites: this.getData()?.getBlackListedSites() ? Array.from(this.getData()?.getBlackListedSites() || []) : undefined,
			white_listed_sites: this.getData()?.getWhiteListedSites() ? Array.from(this.getData()?.getWhiteListedSites() || []) : undefined,

			black_listed_keys: this.getData()?.getBlacklistedKeys() ? Array.from(this.getData()?.getBlacklistedKeys() || []) : undefined,
			white_listed_keys: this.getData()?.getWhitelistedKeys() ? Array.from(this.getData()?.getWhitelistedKeys() || []) : undefined,
		};
	}

	public isValid(options?: any): boolean {
		this.formStore.reset();

		const campaignName = this.getData().getName();
		if (!campaignName) {
			this.formStore.addError(new FormError('name', 'Please provide a campaign name'));
		}

		if (campaignName && !this.getData().getName()) {
			this.formStore.addError(new FormError('name', 'Please provide a campaign name with lower case letters and no spaces'));
		}

		const destinations = this.getData().getCampaignDestinations();
		if (!destinations.length) {
			this.formStore.addError(new FormError('campaign_destinations', 'Please add at least one destination'));
		}

		destinations.forEach(link => {
			if (!link.getSelectedDestinationId()) {
				this.formStore.addError(new FormError('campaign_destinations', 'Please complete destination configuration'));
			}
			if (!link.getDestinationUrl()) {
				this.formStore.addError(new FormError('campaign_destinations', 'Please complete the destination configuration'));
			}
			if (link.getDestinationUseInternalPricing() && !link.getSelectedWebappsCid()) {
				this.formStore.addError(new FormError('webapps_cid', 'Please select a Web Apps CID'));
			}
			if (link.weight != null && (Number.isNaN(Number(link.weight)) || Number(link.weight) < 0)) {
				this.formStore.addError(new FormError('campaign_destinations', 'Destination weights must be non-negative numbers'));
			}
		});

		if (this.getData().getStatus() === Statuses.Active && destinations.length > 0 && !destinations.some(link => link.isActive())) {
			this.formStore.addError(new FormError('campaign_destinations', 'An active campaign must have at least one active destination'));
		}

		if (!this.getData().getAdvertiser()) {
			this.formStore.addError(new FormError('advertiser', 'Please provide an advertiser'));
		}
		if (!this.getData().getCampaignGroup()) {
			this.formStore.addError(new FormError('campaign_group', 'Please provide a campaign group'));
		}

		const phasesMissingCacheKey = this.getData()
			.getPhases()
			.filter(p => !p.getCacheKeyGenerator());
		if (phasesMissingCacheKey.length) {
			const names = phasesMissingCacheKey.map(p => p.phase.name).join(', ');
			this.formStore.addError(
				new FormError('cache_key_generator_phases', `Please provide cache key generator for phase(s): ${names}`)
			);
		}

		if (!this.getData().getCampaignType()) {
			this.formStore.addError(new FormError('campaign_type', 'Please provide campaign type'));
		}

		if (this.getData().getDealType() === DealTypes.OpenAuction && !this.getData().getBillingIds()) {
			this.formStore.addError(new FormError('billing_ids', 'Please provide a billing id'));
		}

		if (!this.getData().getDealType() && !this.getData().getBillingIds()) {
			this.formStore.addError(new FormError('billing_ids', 'Please provide a billing id'));
		}

		if (
			!this.getData()
				.getPhases()
				.every(p => p.strategy?.id)
		) {
			this.formStore.addError(new FormError('strategy', 'Please provide a strategy'));
		}

		if (!this.getData().getTargeting()?.getEnvironment()) {
			this.formStore.addError(new FormError('environment', 'Please provide at least one environment'));
		}

		if (!this.getData().getTargeting()?.getDeviceTypes() || this.getData().getTargeting()?.getDeviceTypes()?.size === 0) {
			this.formStore.addError(new FormError('device_types', 'Please provide at least one device type'));
		}

		if (!this.getData().getTargeting()?.getSellerType()) {
			this.formStore.addError(new FormError('seller_type', 'Please provide a seller type'));
		}
		if (!this.getData().getTargeting()?.getInterstitial()) {
			this.formStore.addError(new FormError('interstitial', 'Please provide a interstitial'));
		}

		if (!this.getData().getTargeting()?.getOperatingSystems()) {
			this.formStore.addError(new FormError('operating_systems', 'Please provide at least one operating system'));
		}

		if (!this.getData().getTargeting()?.getBrowsers()) {
			this.formStore.addError(new FormError('browsers', 'Please provide at least one browser'));
		}

		if (!this.getData().getTargeting()?.getGeos() || this.getData().getTargeting()?.getGeos()?.length === 0) {
			this.formStore.addError(new FormError('geos', 'Please provide at least one country'));
		}
		const formErrors = this.formStore.getErrors();
		if (
			formErrors.has('device_types') &&
			formErrors.has('operating_systems') &&
			formErrors.has('browsers') &&
			formErrors.has('geos') &&
			!this.getData().getTargeting()?.getId()
		) {
			this.formStore.addError(new FormError('targeting_id', 'Please select a targeting'));
		}

		if (this.getData().getStatus() === Statuses.Active) {
			const activationError = this.getDestinationActivationError();
			if (activationError) {
				this.formStore.addError(new FormError('campaign_destinations', activationError));
			}
		}

		// validate domains and pages
		const whiteBlackListDomainsPagesError = getWhiteBlackListDomainsPagesError({
			whitelistDomainsPages: this.getData().getWhiteListedSites(),
			blacklistDomainsPages: this.getData().getBlackListedSites(),
		});
		if (whiteBlackListDomainsPagesError) {
			this.formStore.addError(whiteBlackListDomainsPagesError);
		}

		// validate keys
		const whiteBlackListKeysError = getWhiteBlackListKeysError({
			whitelistKeys: this.getData().getWhitelistedKeys(),
			blacklistKeys: this.getData().getBlacklistedKeys(),
		});
		if (whiteBlackListKeysError) {
			this.formStore.addError(whiteBlackListKeysError);
		}

		// check when editing existing campaign with All and made changes there
		const phasesTouched = JSON.stringify(this.getData().campaign_phases) !== JSON.stringify(this.getOriginData().campaign_phases);

		// must have New and Known phases for new, duplicated campaign or existing campaign with changes in phases
		if (!this.getData().getId() || options?.isDuplicate || phasesTouched) {
			const hasNew = this.getData().campaign_phases?.some(p => p.phase.name === 'NEW');
			const hasKnown = this.getData().campaign_phases?.some(p => p.phase.name === 'KNOWN');
			const hasAll = this.getData().campaign_phases?.some(p => p.phase.name === 'ALL');
			const missingPhases = [!hasNew && '"New"', !hasKnown && '"Known"'].filter(Boolean);

			if (!hasAll) {
				if (missingPhases.length) {
					this.formStore.addError(new FormError('campaign_phases', `Please add ${missingPhases.join(' and ')}`));
				}
			} else if (hasAll && missingPhases.length === 1) {
				if (missingPhases.length) {
					this.formStore.addError(new FormError('campaign_phases', `Please add ${missingPhases.join(' and ')}`));
				}
			}
		}

		return this.formStore.getIsValid();
	}

	private getPreferredDealIds(): id[] {
		const preferredDealIds: id[] = [];
		this.getData()
			?.getDeals()
			?.forEach(campaignPreferredDeal => {
				const preferredDealId = campaignPreferredDeal.getId();
				if (preferredDealId) {
					preferredDealIds.push(preferredDealId);
				}
			});
		return preferredDealIds;
	}

	public async getTargetsList(): Promise<TargetingModel[]> {
		return (await CampaignsApi.getTargets()) || [];
	}

	public async getAmountOfSharedTarget(id: id): Promise<CampaignModel[]> {
		return (await CampaignsApi.getAmountOfSharedTarget(id)) || 0;
	}

	public initEditedState() {
		this.isTargetListingsEdited = false;
	}

	public setTargetListingsEdited() {
		this.isTargetListingsEdited = true;
	}
}
